import posthog, { type PostHog, type Properties } from 'posthog-js'

const PERSISTENCE_NAME = 'movement_analytics'

const sanitizeProps = (props: Properties): Properties => {
  const current = props['$current_url']
  if (typeof current === 'string') {
    try {
      const url = new URL(current)
      props['$current_url'] = url.origin + url.pathname
    } catch {
      // leave as-is if not a parseable URL
    }
  }
  return props
}

// Local instance posts to our Rails backend. Phase 1 endpoint is a no-op, so
// we only run it in development to prove the pipeline works. Phase 2 will
// persist events and we'll turn this on for all environments.
let local: PostHog | null = null

if (window.ENV.RAILS_ENV === 'development') {
  local = posthog.init(
    'local',
    {
      api_host: '/api/posthog_events',
      autocapture: false,
      capture_pageview: true,
      capture_pageleave: true,
      disable_session_recording: true,
      advanced_disable_flags: true,
      // Disable lazy-loaded feature bundles — they'd try to fetch
      // `/api/posthog_events/static/*.js` from our Rails server. Cloud instance
      // handles all this on eu.posthog.com.
      capture_performance: false,
      capture_dead_clicks: false,
      capture_heatmaps: false,
      disable_surveys: true,
      persistence: 'localStorage',
      persistence_name: PERSISTENCE_NAME,
      sanitize_properties: sanitizeProps,
    },
    'local',
  )!
}

let cloud: PostHog | null = null

if (window.ENV.POSTHOG_KEY) {
  cloud = posthog.init(
    window.ENV.POSTHOG_KEY,
    {
      api_host: 'https://eu.posthog.com',
      autocapture: false,
      capture_pageview: true,
      capture_pageleave: true,
      disable_session_recording: false,
      persistence: 'localStorage',
      persistence_name: PERSISTENCE_NAME,
      sanitize_properties: sanitizeProps,
    },
    'cloud',
  )!

  // User IDs are per-shard, so a bare user_id collides across clients. Namespace
  // the distinct_id with the client_id to keep identities disjoint per tenant.
  if (window.ENV.USER_ID && window.ENV.CLIENT_ID) {
    cloud.identify(`${window.ENV.CLIENT_ID}:${window.ENV.USER_ID}`)
  }
  if (window.ENV.CLIENT_ID) cloud.group('client', String(window.ENV.CLIENT_ID), {
    name: window.ENV.CLIENT_NAME,
  })
}

export function getSessionId(): string | undefined {
  return local?.get_session_id() ?? cloud?.get_session_id()
}

export function trackEvent(event: string, properties?: Record<string, unknown>): void {
  local?.capture(event, properties)
  cloud?.capture(event, properties)

  if (local && window.ENV.RAILS_ENV === 'development') {
    console.info(`[PostHog Local] Tracked event: ${event}`, properties)
  }
}
